import { Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"

import _maxBy from "lodash/maxBy"
import _round from "lodash/round"

import React, { ReactElement } from "react"
import useId from "react-use-uuid"
import styled, { keyframes } from "styled-components"
import { isValidColor } from "../../utils/color"

const Wrapper = styled.div`
  margin: 0 auto;
  > header {
    text-align: center;
  }
`

const TitleWrap = styled.div`
  min-height: 68px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
`

const FooterWrap = styled.div<{ width: number }>`
  position: relative;
  padding: 0.5rem 0 1rem;

  > div {
    > span {
      position: absolute;
      width: ${p => p.width / 3}px;
      text-align: center;
      margin-left: -50px;
      display: inline-flex;
      justify-content: center;
      text-transform: uppercase;

      &:nth-child(1) {
        left: ${(100 * 2.5) / 9}%;
      }

      &:nth-child(2) {
        left: ${(100 * 6.5) / 9}%;
      }
    }
  }
`

const GrowthBarsWrap = styled.div`
  > svg {
  }
`

const ValueText = styled.text`
  text-anchor: middle;
  font-size: 1.6rem;

  > tspan > tspan {
    font-size: 1.3rem;
    opacity: 0.6;
  }
`

const zoomEnter = keyframes`
  0% {
    opacity: 0;
    transform: scale(0, 50%, 50%);
  }
  100% {
    opacity: 1;
    transform: scale(1, 50%, 50%);
  }
`

const MultipleValueText = styled(ValueText)`
  font-size: 1rem;

  > tspan {
    animation: ${zoomEnter} 5s forwards;
  }
`

const Bar = styled.rect``

type TGrowthBarDatum = {
  label?: string
  value: number
}

interface IGrowthBarsProps {
  /**
   * Bar color, which can be a css color, or a theme palette key (primary, secondary, error, etc)
   */
  colors: string[]
  /**
   * Bars data
   * @default []
   */
  data: TGrowthBarDatum[]
  /**
   * Description to be displayed under the title
   * @default ""
   */
  description?: string
  /**
   * Optional max value to scale taller bar. Otherwise highest bar will occupy full height.
   */
  maxValForScale?: number
  /**
   * Round to this many dp when displaying the multiplier
   * @default 1
   */
  multipleDp?: number
  /**
   * The type of multiplier to show. +50% or x1.5
   * @default "%"
   */
  multiplierType: "%" | "x"
  /**
   * Additional styles to be added to the wrapper for layout purposes
   * @default []
   */
  styles: React.CSSProperties
  /**
   * A title to be displayed above the chart
   * @default ""
   */
  title?: string
  /**
   * A prefix to add to each cell
   * @default ""
   */
  valuePrefix?: string
  /**
   * A suffix to add to each cell
   * @default ""
   */
  valueSuffix?: string
  /**
   * Component width
   * @default 400
   */
  width: number
}

export const GrowthBars = ({
  colors,
  data = [],
  description = "",
  styles = {},
  title = "",
  valueSuffix = "",
  valuePrefix = "",
  width = 400,
  maxValForScale,
  multipleDp = 1,
  multiplierType = "%"
}: IGrowthBarsProps) => {
  const theme = useTheme()
  const id: string = useId()
  const unitWidth: number = width / 9

  if (data.length !== 2) {
    return "Data length must be 2"
  }

  const maxElm = _maxBy(data, "value")

  if (typeof maxElm === "undefined") {
    return "Could not determine max value in data"
  }
  const maxVal = maxElm.value

  const isIncrease: boolean = maxVal === data[1].value

  let multiple: string = `${_round(
    (100 * data[1].value) / data[0].value - 100,
    multipleDp || 0
  )}%`
  if (isIncrease) {
    multiple = `+${multiple}`
  }

  if (multiplierType === "x") {
    multiple = `${_round(data[1].value / data[0].value, multipleDp || 1)}x`
  }

  if (maxValForScale && maxVal > maxValForScale) {
    return "maxValForScale must be greater than all values in data"
  }

  const barColors: string[] =
    colors && colors.length === 2 ? colors : ["primary", "primary"]

  // check if color is palette key
  if (
    barColors[0] &&
    !isValidColor(barColors[0]) &&
    barColors[0] in theme.palette
  ) {
    barColors[0] = theme.palette[barColors[0]].dark
  }
  if (
    barColors[1] &&
    !isValidColor(barColors[1]) &&
    barColors[1] in theme.palette
  ) {
    barColors[1] = theme.palette[barColors[1]].main
  }

  const layoutData = data.map((d) => {
    const fract = maxValForScale ? d.value / maxValForScale : d.value / maxVal
    const barHeight = width * fract
    return {
      ...d,
      y: width - barHeight,
      height: barHeight,
      valueLabelY: width - barHeight + 40
    }
  })

  const x1 = isIncrease ? unitWidth * 2.5 : unitWidth * 6.5
  const connectorProps = {
    x1,
    y1: isIncrease ? layoutData[1].y : layoutData[0].y,
    x2: x1,
    y2: isIncrease ? layoutData[0].y : layoutData[1].y,
    markerStart: "none",
    markerEnd: "none"
  }

  if (isIncrease) {
    connectorProps.markerStart = `url(#arrow-head-increase--${id})`
  } else {
    connectorProps.markerEnd = `url(#arrow-head-decrease--${id})`
  }

  const connector2Props = {
    x1: connectorProps.x1,
    x2: connectorProps.x1 + (isIncrease ? unitWidth * 2.5 : -unitWidth * 2.5),
    y1: connectorProps.y1,
    y2: connectorProps.y1,
    strokeDasharray: "2,2"
  }

  const multipleLabelProps = {
    x: connectorProps.x1,
    y: connectorProps.y1 + 10 + (connectorProps.y2 - connectorProps.y1) * 0.5
  }

  return (
    <Wrapper style={{ ...styles, width }}>
      <header>
        {title ? (
          <>
            <TitleWrap>
              <Typography variant="h4">{title}</Typography>
              {description ? (
                <Typography gutterBottom>{description}</Typography>
              ) : (
                ""
              )}
            </TitleWrap>
          </>
        ) : (
          ""
        )}
      </header>
      <GrowthBarsWrap>
        <svg width={width} height={width} viewBox={`0 0 ${width} ${width}`}>
          <g id={`bar-1--${id}`}>
            <Bar
              width={unitWidth * 3}
              height={layoutData[0].height}
              x={unitWidth}
              y={layoutData[0].y}
              fill={`url(#stripes-dark-${id})`}
            />
            <ValueText x={0} y={0} fill={"white"}>
              <tspan x={unitWidth * 2.5} y={layoutData[0].valueLabelY}>
                <tspan>{valuePrefix}</tspan>
                {layoutData[0].value}
                <tspan>{valueSuffix}</tspan>
              </tspan>
            </ValueText>
          </g>

          <g id={`bar-2--${id}`}>
            <Bar
              width={unitWidth * 3}
              height={layoutData[1].height}
              x={unitWidth * 5}
              y={layoutData[1].y}
              fill={`url(#stripes-main-${id})`}
              mask={isIncrease ? `url(#increase-mask--${id})` : "none"}
            />
            <ValueText x={0} y={0} fill={"white"}>
              <tspan x={unitWidth * 6.5} y={layoutData[1].valueLabelY}>
                <tspan>{valuePrefix}</tspan>
                {layoutData[1].value}
                <tspan>{valueSuffix}</tspan>
              </tspan>
            </ValueText>
          </g>

          <g id={`connecting-line--${id}`}>
            <line stroke={theme.basePalette.fg} {...connectorProps} />
            <line stroke={theme.basePalette.fg} {...connector2Props} />
            <MultipleValueText
              x={0}
              y={0}
              fill={theme.basePalette.fg}
              filter={`url(#multiple-text-${id})`}
            >
              <tspan {...multipleLabelProps}>{multiple}</tspan>
            </MultipleValueText>
          </g>

          <defs>
            <marker
              id={`arrow-head-decrease--${id}`}
              orient="auto"
              markerWidth="10"
              markerHeight="20"
              refX="10"
              refY="10"
            >
              <path d="M0,0 V20 L10,10 Z" fill={theme.basePalette.fg} />
            </marker>
            <marker
              id={`arrow-head-increase--${id}`}
              orient="auto-start-reverse"
              markerWidth="10"
              markerHeight="20"
              refX="10"
              refY="10"
            >
              <path d="M0,0 V20 L10,10 Z" fill={theme.basePalette.fg} />
            </marker>
            {theme.viz.SVG.filters.floodedBackground({
              id: `multiple-text-${id}`,
              color: theme.basePalette.bg
            })}
            {theme.viz.SVG.patterns.createStripePattern({
              id: `stripes-dark-${id}`,
              fill: barColors[0],
              textureOpacity: 0.1
            })}
            {theme.viz.SVG.patterns.createStripePattern({
              id: `stripes-main-${id}`,
              fill: barColors[1],
              textureOpacity: 0.1
            })}
            <mask id={`increase-mask--${id}`}>
              <rect
                fill={"white"}
                fillOpacity={1}
                x={0}
                y={0}
                width={width}
                height={width}
              />
              <rect
                fill={"black"}
                fillOpacity={0.1}
                x={0}
                y={layoutData[0].y}
                width={width}
                height={width}
              />
            </mask>
          </defs>
        </svg>
      </GrowthBarsWrap>
      <FooterWrap width={width}>
        {data[0].label ? (
          <div>
            <Typography variant="caption">{data[0].label}</Typography>
            <Typography variant="caption">{data[1].label}</Typography>
          </div>
        ) : (
          ""
        )}
      </FooterWrap>
    </Wrapper>
  )
}

export default GrowthBars
